<div class="modal-header">
  <h5 class="modal-title">{{ 'membershipPricing.title' | translate }}</h5>
  <button type="button" class="btn-close" aria-label="Close" (click)="close()"></button>
</div>

<div class="modal-body">
  @if (loading) {
    <div class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">{{ 'common.loading' | translate }}</span>
      </div>
    </div>
  } @else {
    <div class="mb-3">
      <div class="d-flex justify-content-between align-items-center">
        <span class="fw-semibold">{{ itemName }}</span>
        <span class="text-muted">{{ 'membershipPricing.basePrice' | translate }}: {{ formatBasePrice() }} {{ currency }}</span>
      </div>
    </div>

    <p class="text-muted small mb-3">
      {{ 'membershipPricing.description' | translate }}
    </p>

    @if (companyGroups.length === 0) {
      <div class="alert alert-info">
        {{ 'membershipPricing.noPlans' | translate }}
      </div>
    } @else {
      <!-- Organisation Groups -->
      <div class="organisation-groups">
        @for (group of companyGroups; track group.companyId) {
          <div class="card mb-3" [class.border-primary]="group.selectedCount > 0">
            <!-- Organisation Header (Clickable) -->
            <div class="card-header cursor-pointer d-flex align-items-center justify-content-between py-3" (click)="toggleGroup(group)">
              <div class="d-flex align-items-center flex-grow-1">
                <!-- Logo -->
                <div class="organisation-logo me-3" style="width: 40px; height: 40px;">
                  @if (group.companyLogo) {
                    <img [src]="group.companyLogo" [alt]="group.companyName" class="rounded" style="width: 40px; height: 40px; object-fit: cover;" />
                  } @else {
                    <div class="bg-light rounded d-flex align-items-center justify-content-center" style="width: 40px; height: 40px;">
                      <i class="fa fa-building text-muted"></i>
                    </div>
                  }
                </div>

                <!-- Organisation Info -->
                <div class="flex-grow-1">
                  <h6 class="mb-0 fw-bold">{{ group.companyName }}</h6>
                  <small class="text-muted">
                    {{ group.entries.length }} {{ group.entries.length === 1 ? ('membershipPricing.plan' | translate) : ('membershipPricing.plans' | translate) }}
                    @if (group.selectedCount > 0) {
                      <span class="text-primary ms-2">
                        ({{ group.selectedCount }} {{ 'membershipPricing.selected' | translate }})
                      </span>
                    }
                  </small>
                </div>
              </div>

              <!-- Expand Icon -->
              <i class="fa" [class.fa-chevron-down]="!group.isExpanded" [class.fa-chevron-up]="group.isExpanded"></i>
            </div>

            <!-- Plans (Expandable) -->
            @if (group.isExpanded) {
              <div class="card-body pt-0 pb-0 ps-3 pe-0">
                <div class="table-responsive">
                  <table class="table table-hover mb-0">
                    <thead class="table-light">
                      <tr>
                        <th style="width: 40px;"></th>
                        <th>{{ 'membershipPricing.membershipPlan' | translate }}</th>
                        <th style="width: 150px;">{{ 'membershipPricing.memberPrice' | translate }}</th>
                        <th style="width: 100px;">{{ 'membershipPricing.savings' | translate }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      @for (entry of group.entries; track entry.plan.plan_id) {
                        <tr [class.table-active]="entry.isSelected">
                          <td>
                            <div class="form-check">
                              <input
                                class="form-check-input"
                                type="checkbox"
                                [checked]="entry.isSelected"
                                (change)="togglePlan(entry, group)"
                                [id]="'plan-' + entry.plan.plan_id"
                              >
                            </div>
                          </td>
                          <td>
                            <label [for]="'plan-' + entry.plan.plan_id" class="cursor-pointer mb-0">
                              <span class="fw-medium">{{ entry.plan.name }}</span>
                              @if (entry.plan.coverage) {
                                <span class="badge badge-sm ms-2" [class.badge-light-primary]="entry.plan.coverage === 'individual'" [class.badge-light-success]="entry.plan.coverage === 'family'">
                                  {{ entry.plan.coverage | titlecase }}
                                </span>
                              }
                              @if (entry.plan.description) {
                                <br>
                                <small class="text-muted">{{ entry.plan.description }}</small>
                              }
                            </label>
                          </td>
                          <td>
                            @if (entry.isSelected) {
                              <div class="input-group input-group-sm">
                                <input
                                  type="number"
                                  class="form-control"
                                  [(ngModel)]="entry.price"
                                  [placeholder]="'membershipPricing.amount' | translate"
                                  min="0"
                                  step="1"
                                >
                                <span class="input-group-text">{{ currency }}</span>
                              </div>
                            } @else {
                              <span class="text-muted">-</span>
                            }
                          </td>
                          <td>
                            @if (entry.isSelected && entry.price !== null && entry.price >= 0) {
                              <span class="badge" [class.bg-success]="getSavingsPercent(entry.price) > 0" [class.bg-secondary]="getSavingsPercent(entry.price) <= 0">
                                {{ getSavingsPercent(entry.price) }}% {{ 'membershipPricing.off' | translate }}
                              </span>
                            } @else {
                              <span class="text-muted">-</span>
                            }
                          </td>
                        </tr>
                      }
                    </tbody>
                  </table>
                </div>
              </div>
            }
          </div>
        }
      </div>
    }
  }
</div>

<div class="modal-footer">
  <button type="button" class="btn btn-outline-secondary" (click)="close()">{{ 'common.cancel' | translate }}</button>
  <button
    type="button"
    class="btn btn-primary"
    (click)="save()"
    [disabled]="saving || loading"
  >
    @if (saving) {
      <span class="spinner-border spinner-border-sm me-1" role="status"></span>
      {{ 'common.saving' | translate }}
    } @else {
      {{ 'membershipPricing.savePrices' | translate }}
    }
  </button>
</div>
